// InviteAndGrant — invite a person into the current workspace, pure.
//
// RECONCILED to the LIVE FLAT backend (IDD 94442b9d): `POST
// /workspaces/:id/members {email}` grants exactly one role, "member" — there
// is no scope picker (a single-level workspace has nothing to pick between)
// and no role picker (nothing to choose). One field, one action: an email
// address. If it belongs to someone who has never signed in, the invite
// creates their user row now and the grant is waiting on their first
// sign-in — sign-in and invite-acceptance are the SAME act, no
// separate "accept" step to design for.
//
// Pure/props-driven, fully controlled: `draft` lives outside the component;
// every field change reports UP.

export type { InviteAndGrantDraft, InviteAndGrantProps };
export { InviteAndGrant };

import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "../../Button/Button.js";
import { Dialog } from "../../Dialog/Dialog.js";
import { Field } from "../../Field/Field.js";
import { Input } from "../../Input/Input.js";
import { Text } from "../../Text/Text.js";
import "./InviteAndGrant.css";

type InviteAndGrantDraft = { readonly email: string };

type InviteAndGrantProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly draft: InviteAndGrantDraft;
    readonly onDraftChange: (draft: InviteAndGrantDraft) => void;
    readonly onSubmit: (email: string) => void;
    readonly submitting?: boolean;
};

// Good-enough shape check for enabling submit — real validation belongs to
// the identity port, not here.
const isPlausibleEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const INVITE_EMAIL_FIELD_ID = "iam-invite-email";

const InviteAndGrant = ({
    open,
    onOpenChange,
    draft,
    onDraftChange,
    onSubmit,
    submitting,
}: InviteAndGrantProps) => {
    const { t } = useLingui();
    const emailValid = isPlausibleEmail(draft.email);

    const submit = () => {
        if (!emailValid || submitting) return;
        onSubmit(draft.email.trim());
    };

    return (
        <Dialog
            open={open}
            onOpenChange={onOpenChange}
            title={t`Invite to this workspace`}
            description={t`They get a sign-in link. They become a member as soon as they sign in — the same way every account is created.`}
            footer={
                <>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        disabled={!emailValid || submitting}
                        onClick={submit}
                    >
                        {submitting ? (
                            <Trans>Sending…</Trans>
                        ) : (
                            <Trans>Send invite</Trans>
                        )}
                    </Button>
                </>
            }
        >
            <div className="bilda-iam-invite">
                <Field label={t`Email`} htmlFor={INVITE_EMAIL_FIELD_ID}>
                    <Input
                        id={INVITE_EMAIL_FIELD_ID}
                        type="email"
                        autoFocus
                        placeholder={t`name@school.edu`}
                        value={draft.email}
                        onChange={(event) =>
                            onDraftChange({ email: event.currentTarget.value })
                        }
                        invalid={draft.email.length > 0 && !emailValid}
                    />
                </Field>
                <Text tone="faint" size="xs">
                    <Trans>
                        They join as "Member" — the only role there is today.
                    </Trans>
                </Text>
            </div>
        </Dialog>
    );
};
